import { SmartPdfSubmissionResultSchema } from './types';
import { ApiEndpointClient } from './resource-client';
const VALUATION_API_PREFIX = 'valuation-api/v1';
function getDeviationAnalysisQuery({ baseValuationId, comparisonValuationId, baseValueCheckId, comparisonValueCheckId, orderId, customerId, }) {
    return {
        ...(baseValueCheckId
            ? { base_value_check_uuid: baseValueCheckId }
            : baseValuationId !== undefined
                ? { base_valuation_uuid: baseValuationId }
                : {}),
        ...(comparisonValueCheckId
            ? { comparison_value_check_uuid: comparisonValueCheckId }
            : comparisonValuationId !== undefined
                ? { comparison_valuation_uuid: comparisonValuationId }
                : {}),
        ...(orderId && { order_uuid: orderId }),
        ...(customerId && { customer_uuid: customerId }),
    };
}
export class ValuationsClient extends ApiEndpointClient {
    http;
    constructor(http) {
        super(http, VALUATION_API_PREFIX);
        this.http = http;
    }
    createValuationClone = async ({ valuationId, orderId, }) => {
        return this.post(`/valuations/${valuationId}/clone`, {
            json: { order_uuid: orderId },
        });
    };
    getCustomerValuation = async ({ valuationId, customerId, }) => {
        return this.get(`/valuations/${valuationId}`, {
            query: customerId ? { customer_uuid: customerId } : undefined,
        });
    };
    getCustomerValuations = async ({ assetId, includeDrafts, customerId, }) => {
        return this.get('/valuations/', {
            query: {
                ...(assetId ? { asset_uuid: assetId } : {}),
                ...(includeDrafts ? { include_drafts: true } : {}),
                ...(customerId ? { customer_uuid: customerId } : {}),
            },
        });
    };
    getDownloadAllValuationDocumentsLinkToZip = async ({ valuationId }) => {
        return this.get(`/valuations/${valuationId}/documents-attachments`);
    };
    getDownloadAllValuationImagesLinkToZip = async ({ valuationId }) => {
        return this.get(`/valuations/${valuationId}/images-attachments`);
    };
    getValXmlExportLink = async ({ valuationId, customerId }) => {
        return this.get(`/valuations/${valuationId}/valxml-export`, {
            query: customerId ? { customer_uuid: customerId } : undefined,
        });
    };
    createValuationFromAsset = async ({ assetId, valuationType, reportType, }) => {
        return this.post('/valuations/', {
            json: {
                asset_uuid: assetId,
                valuation_type: valuationType,
                report_type: reportType,
            },
        });
    };
    createValuationFromOrder = async ({ orderId, valuationType, reportType, }) => {
        return this.post('/valuations/', {
            json: {
                order_uuid: orderId,
                valuation_type: valuationType,
                report_type: reportType,
            },
        });
    };
    getValuation = async ({ valuationId }, options) => {
        return this.get(`/valuations/${valuationId}`, { signal: options?.signal });
    };
    getValuations = async ({ orderId, assetId, includeDrafts } = {}, options) => {
        return this.get('/valuations/', {
            query: {
                ...(orderId ? { order_uuid: orderId } : {}),
                ...(assetId ? { asset_uuid: assetId } : {}),
                ...(includeDrafts ? { include_drafts: true } : {}),
            },
            signal: options?.signal,
        });
    };
    getDeviationAnalysis = async (params) => {
        return this.get('/valuations/deviation-analysis/', {
            query: getDeviationAnalysisQuery(params),
        });
    };
    updateValuationFromValXml = async ({ valuationId, xml, filename, }) => {
        return this.put(`/valuations/${valuationId}`, {
            body: xml,
            headers: {
                'Content-Type': 'application/x.valxml+xml',
                'X-Original-Filename': encodeURIComponent(filename),
            },
        });
    };
    createExternalValuationFromOrder = async ({ orderId, reportType, submissionTrigger, }) => {
        return this.post('/valuations/', {
            json: {
                order_uuid: orderId,
                valuation_type: 'external',
                report_type: reportType,
                submission_trigger: submissionTrigger,
            },
        });
    };
    replaceValuationFromValXml = async ({ valuationId, file, }) => {
        return this.put(`/valuations/${valuationId}`, {
            body: file,
            headers: {
                'Content-Type': 'application/x.valxml+xml',
                'X-Original-Filename': encodeURIComponent(file.name),
            },
        });
    };
    patchValuationFromValXml = async ({ valuationId, file }) => {
        await this.patch(`/valuations/${valuationId}`, {
            response: 'raw',
            body: file,
            headers: {
                'Content-Type': 'application/x.valxml+xml',
                'X-Original-Filename': encodeURIComponent(file.name),
            },
        });
    };
    patchValuation = async ({ valuationId, operations, valXmlPayload, queryParams, }) => {
        await this.patch(`/valuations/${valuationId}`, {
            query: queryParams,
            response: 'raw',
            ...(valXmlPayload
                ? {
                    body: valXmlPayload.xml,
                    headers: {
                        'Content-Type': 'application/x.valxml+xml',
                        'X-Original-Filename': encodeURIComponent(valXmlPayload.filename),
                    },
                }
                : { json: operations }),
        });
    };
    deleteValuation = async ({ valuationId, deleteReason }) => {
        await this.delete(`/valuations/${valuationId}`, { json: { delete_reason: deleteReason }, response: 'raw' });
    };
    uploadValuationImage = async ({ valuationId, image }) => {
        const formData = new FormData();
        formData.append('file', image);
        return this.post(`/valuations/${valuationId}/images`, { body: formData, response: 'raw' });
    };
    uploadDocument = async ({ valuationId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return this.post(`/valuations/${valuationId}/documents`, { body: formData });
    };
    uploadComparablesSourceFile = async ({ valuationId, economicUnitIndex, sourceIndex, file, }) => {
        const formData = new FormData();
        formData.append('file', file);
        return this.post(`/valuations/${valuationId}/source_attachments`, {
            body: formData,
            query: {
                ...(economicUnitIndex !== undefined ? { economic_unit_index: economicUnitIndex.toString() } : {}),
                source_index: sourceIndex.toString(),
            },
        });
    };
    uploadReportPdf = async ({ valuationId, file }) => {
        return this.postFile(`/valuations/${valuationId}/report-file`, file);
    };
    submitSmartPdf = async ({ valuationId, file, target, }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('target', target);
        const response = await this.put(`/valuations/${valuationId}/smart-pdf-submission`, { body: formData, response: 'raw' });
        return SmartPdfSubmissionResultSchema.parse(await response.json());
    };
    createDraft = async ({ valuationId }) => {
        return this.post(`/valuations/${valuationId}/draft`, { json: {} });
    };
}
